import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import HoomanCard from "@/components/HoomanCard";
import { Search, X, Briefcase, ArrowRight, Flame, MapPin } from "lucide-react";
import { CATEGORY_GROUPS } from "@/lib/categories";
import { useI18n } from "@/lib/i18n";
import { formatSalary, employmentLabel } from "@/lib/jobs";

export default function Discover() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [openGroup, setOpenGroup] = useState(null); // null = all
  const [activeCat, setActiveCat] = useState(null);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");

  const load = async () => {
    const params = {};
    if (activeCat) params.category = activeCat;
    if (q.trim()) params.q = q.trim();
    if (loc.trim()) params.location = loc.trim();
    const { data } = await api.get("/users", { params });
    setUsers(data);
  };

  const loadJobs = async () => {
    const params = { limit: 6 };
    if (q.trim()) params.q = q.trim();
    if (loc.trim()) params.location = loc.trim();
    try {
      const { data } = await api.get("/jobs", { params });
      setJobs(data || []);
    } catch { /* jobs are optional on discover */ }
  };

  useEffect(() => { load(); loadJobs(); }, [activeCat]);

  const clearAll = () => { setActiveCat(null); setOpenGroup(null); };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="overline mb-3">{t("nav.discover")}</p>
          <h1 className="h-display text-4xl sm:text-5xl">{t("discover.title")}</h1>
          <p className="font-heading text-xl text-[#4A4A4A] mt-2">{t("discover.subtitle")}</p>
        </div>
        <form onSubmit={(e)=>{e.preventDefault(); load(); loadJobs();}} className="flex gap-2 w-full md:w-auto" data-testid="discover-search-form">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={16}/>
            <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder={t("discover.search")} className="input pl-9" data-testid="discover-search-q" />
          </div>
          <input value={loc} onChange={(e)=>setLoc(e.target.value)} placeholder={t("discover.location")} className="input" data-testid="discover-search-loc" />
          <button className="btn-primary" data-testid="discover-search-submit">{t("discover.searchBtn")}</button>
        </form>
      </div>

      {/* Group pills */}
      <div className="mt-10 flex gap-2 flex-wrap" data-testid="discover-groups">
        <button onClick={clearAll}
          className={`pill-link ${!openGroup ? "active":""}`} data-testid="discover-group-all">
          {t("discover.all")}
        </button>
        {CATEGORY_GROUPS.map(g => (
          <button key={g.id} onClick={()=>{ setOpenGroup(g.id===openGroup?null:g.id); setActiveCat(null);}}
            className={`pill-link ${openGroup===g.id?"active":""}`} data-testid={`discover-group-${g.id}`}>
            {g.label}
          </button>
        ))}
      </div>

      {/* Sub-categories drawer */}
      {openGroup && (
        <div className="mt-5 card p-5 rise" data-testid="discover-subcats">
          <div className="flex items-center justify-between mb-3">
            <p className="overline">{CATEGORY_GROUPS.find(g=>g.id===openGroup)?.label}</p>
            <button onClick={()=>{setOpenGroup(null); setActiveCat(null);}} className="btn-ghost text-xs" data-testid="discover-subcats-close"><X size={14}/> Close</button>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {CATEGORY_GROUPS.find(g=>g.id===openGroup)?.items.map(c=>(
              <button key={c} onClick={()=>setActiveCat(c===activeCat?null:c)}
                className={`text-sm px-3 py-1.5 rounded-full border transition-all
                  ${activeCat===c ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#E5E5E3] text-[#0A0A0A] hover:bg-[#F2F2F0]"}`}
                data-testid={`discover-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>{c}</button>
            ))}
          </div>
        </div>
      )}

      {/* Hoomans grid — first */}
      <div className="mt-10 mb-3">
        <p className="overline">Hoomans · {users.length}</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="discover-grid">
        {users.map((u) => <HoomanCard key={u.id} u={u} />)}
      </div>
      {users.length === 0 && (
        <div className="text-center mt-10">
          <p className="text-[#4A4A4A]">No Hoomanz match yet.</p>
          <button onClick={clearAll} className="btn-secondary mt-4">Clear filters</button>
        </div>
      )}

      {/* Jobs strip — always visible, below Hoomans */}
      <section className="mt-16" data-testid="discover-jobs">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <p className="overline mb-1 inline-flex items-center gap-1.5">
              <Briefcase size={12}/> Jobs on Hoomanz
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl">Roles hiring real humans.</h2>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/jobs/new" className="btn-ghost text-sm" data-testid="discover-jobs-post">
              Post a job
            </Link>
            <Link to="/jobs" className="btn-ghost text-sm" data-testid="discover-jobs-seeall">
              See all jobs <ArrowRight size={14}/>
            </Link>
          </div>
        </div>

        {jobs.length === 0 ? (
          <div className="card p-7 text-center" data-testid="discover-jobs-empty">
            <Briefcase size={22} className="mx-auto text-[#0057FF] mb-2"/>
            <p className="text-sm text-[#4A4A4A]">No jobs match yet. Be the first to post one.</p>
            <Link to="/jobs/new" className="btn-secondary inline-flex mt-3 !py-2 !px-4 text-sm">Post a job</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discover-jobs-grid">
            {jobs.map((j) => (
              <Link
                to={`/jobs/${j.id}`}
                key={j.id}
                data-testid={`discover-job-${j.id}`}
                className={`card p-5 hover:border-[#0A0A0A] transition-colors ${j.is_boosted ? "ring-1 ring-[#0057FF]/40" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <img src={j.employer?.avatar} alt="" className="h-10 w-10 rounded-full object-cover bg-[#E5E5E3] shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h3 className="font-medium leading-tight truncate">{j.title}</h3>
                      {j.is_boosted && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0057FF] text-white inline-flex items-center gap-1">
                          <Flame size={9}/> {j.boost_tier}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#8A8A8A] mt-0.5 truncate">
                      {j.company_name || j.employer?.name}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-[#4A4A4A] mt-3 inline-flex items-center gap-1">
                  <MapPin size={11}/> {j.location || "Remote"}
                  <span className="mx-1.5 opacity-50">·</span>
                  {employmentLabel(j.employment_type)}
                </p>
                <p className="text-sm font-medium mt-2">{formatSalary(j)}</p>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
